import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, ShieldAlert } from "lucide-react";

/**
 * T1ConfirmReviewedDialog — QUESTIONNAIRE-2.0-A-S2-β-A (stand-alone).
 *
 * Reusable modal for two flows:
 *   mode="promote"  → mark a translation as `reviewed`
 *   mode="demote"   → revert a `reviewed` translation back to `draft`
 *
 * Hard rules (analysis §12, §16):
 *   - Checkbox is **mandatory** to enable the confirm button.
 *   - No "Don't show again".
 *   - No auto-tick across renders.
 *   - The dialog NEVER calls the backend itself — onConfirm is async and
 *     the parent decides what PATCH to issue. Errors stay surface inside
 *     the dialog so the admin can retry without losing context.
 */

const T1ConfirmReviewedDialog = ({
  open,
  mode = "promote",          // "promote" | "demote"
  field,                      // { path, langDisplay, value }
  fromStatus,                 // current status (informational)
  onCancel,
  onConfirm,                  // async () => void — throws to keep dialog open
  busy = false,
  errorMessage = null,        // string or null — surfaced inside the dialog
}) => {
  const [checked, setChecked] = useState(false);

  // Note: the parent is expected to unmount this dialog on close (via the
  // `open` prop) so that checkbox state naturally resets between sessions.
  // We deliberately avoid a useEffect-reset to comply with the project's
  // `react-hooks/set-state-in-effect` rule.

  const handleOpenChange = (o) => {
    if (!o && !busy) {
      setChecked(false);
      onCancel?.();
    }
  };

  const handleCancelClick = () => {
    setChecked(false);
    onCancel?.();
  };

  const isPromote = mode === "promote";
  const titleText = isPromote ? "Mark as reviewed" : "Demote reviewed → draft";
  const Icon = isPromote ? ShieldCheck : ShieldAlert;
  const accent = isPromote ? "text-emerald-600" : "text-amber-700";
  const buttonLabel = isPromote ? "Mark reviewed" : "Demote to draft";
  const buttonStyle = isPromote
    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
    : "bg-amber-600 hover:bg-amber-700 text-white";

  const checkboxLabel = isPromote
    ? "I have reviewed this text against the base EN content and confirm it is publication-ready."
    : "I confirm this translation needs more work and should return to draft.";

  const canConfirm = checked && !busy;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="t1-reviewed-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${accent}`} />
            <span>{titleText}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            This action is auditable. Concurrent edits are protected via optimistic concurrency.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-xs">
          <div>
            <span className="text-slate-500">Field path:</span>{" "}
            <code data-testid="t1-reviewed-dialog-field-path" className="font-mono break-all text-slate-800">
              {field?.path || "—"}
            </code>
          </div>
          <div>
            <span className="text-slate-500">Language:</span>{" "}
            <span data-testid="t1-reviewed-dialog-field-lang" className="font-semibold text-slate-800">
              {field?.langDisplay || "—"}
            </span>
          </div>
          <div>
            <span className="text-slate-500">From status:</span>{" "}
            <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700">
              {fromStatus || "—"}
            </code>
          </div>
          <div>
            <span className="text-slate-500">Current value:</span>
            <div
              data-testid="t1-reviewed-dialog-field-value"
              className="mt-1 max-h-32 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2 font-mono text-[11px] text-slate-700"
            >
              {field?.value ? field.value : <span className="italic text-slate-400">(empty)</span>}
            </div>
          </div>
        </div>

        <label className="mt-2 flex items-start gap-2 rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700">
          <input
            type="checkbox"
            data-testid="t1-reviewed-dialog-checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5"
            disabled={busy}
          />
          <span>{checkboxLabel}</span>
        </label>

        {errorMessage && (
          <p
            data-testid="t1-reviewed-dialog-error"
            className="rounded border border-rose-300 bg-rose-50 px-2 py-1 text-[11px] text-rose-700"
          >
            {errorMessage}
          </p>
        )}

        <DialogFooter className="gap-2">
          <button
            type="button"
            data-testid="t1-reviewed-dialog-cancel-btn"
            onClick={handleCancelClick}
            disabled={busy}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="t1-reviewed-dialog-confirm-btn"
            onClick={() => onConfirm?.()}
            disabled={!canConfirm}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${buttonStyle} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {busy ? (
              <span data-testid="t1-reviewed-dialog-spinner">Saving…</span>
            ) : (
              buttonLabel
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default T1ConfirmReviewedDialog;
